import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from "@angular/core";
import { PersonRole } from "@ha/data/basic";
import { BaseModalComponent, ModalComponent } from "@ha/ui/common";
import { PwaState } from "@ha/util/pwa";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Actions, ofActionSuccessful, Select, Store } from "@ngxs/store";
import { Observable } from "rxjs";
import { PermitCreateSelectors } from "../../../permit-create/states/permit-create.selectors";

import { PermitPerson, PermitPersonWithStatus } from "../../../shared/models/permit-person.model";
import { CreateCertificateCommand, CreateCertificateForm } from "../../models/create-certificate-person.model";
import { PermitTeam } from "../../models/permit-team.model";
import { PermitTeamActions } from "../../states/permit-team.action";
import { PermitTeamSelectors } from "../../states/permit-team.selectors";
import { CreateCertificateComponent } from "../create-certificate/create-certificate.component";
import { LastUsedPersonsListComponent } from "../last-used-persons-list/last-used-persons-list.component";
import { AddPersonModalComponentData } from "./interface/add-person-modal-component-data";


export enum AddPersonView {
    LastUsed = 1,
    Search = 2,
    Other = 3,
    NoFirewatcher = 4,
    CreateCertificate = 5,
    Text = 6,
}

@UntilDestroy()
@Component({
    templateUrl: "add-person-modal.component.html",
    styleUrls: ["add-person-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddPersonModalComponent extends BaseModalComponent {
    @ViewChild("alertModal")
    private alertModal: ModalComponent;
    @ViewChild("lastUsed")
    private lastUsed: LastUsedPersonsListComponent;
    @ViewChild("createCertificate")
    public createCertificate: CreateCertificateComponent;

    public requireEmailAddress = false;

    public get permitId() {
        return this.data.permitId;
    }

    public get role() {
        return this.data.role;
    }

    public get showFireWatcherNotNeeded() {
        if (this.store.selectSnapshot(PermitCreateSelectors.fireWatcherRequired)) {
            return false;
        } else if (this.data.detailsMode) {
            return false;
        }

        return true;
    }

    public person: PermitPersonWithStatus;
    public data: AddPersonModalComponentData;
    public PersonRole = PersonRole;
    public noScroll = false;
    public form: CreateCertificateForm;
    public formValid = false;
    public actionsCreateNewCertificateAndStagePerson = PermitTeamActions.CreateNewCertificateAndStagePerson;

    @Select(PermitTeamSelectors.team)
    public permitTeam$: Observable<PermitTeam>;

    @Select(PermitTeamSelectors.stagedPersons)
    public stagedPersons$: Observable<PermitPerson[]>;

    @Select(PermitCreateSelectors.isFlammableHotWork)
    public isFlammableHotWork$: Observable<boolean>;

    public currentView: AddPersonView = AddPersonView.LastUsed;
    public View = AddPersonView;

    public get title(): string {
        if (this.role == PersonRole.FireWatcher) {
            return "titles.firewatcher";
        } else if (this.role == PersonRole.HotWorker) {
            return "titles.hotworker";
        } else if (this.role == PersonRole.Superintendent) {
            return "titles.superintendent";
        } else if (this.role == PersonRole.PermitIssuer) {
            return "titles.permitissuer";
        } else if (this.role == PersonRole.PostFireWatcher) {
            return "titles.postfirewatcher";
        } else {
            return "error";
        }
    }

    constructor(private store: Store, private cdr: ChangeDetectorRef) {
        super();
    }

    public addStagedPersons(): void {
        this.store.dispatch(new PermitTeamActions.AddStagedPersonsToPermit(this.permitId, this.role));
        this.close();
    }

    public abort(): void {
        const stagedPersons = this.store.selectSnapshot(PermitTeamSelectors.stagedPersons);
        if (stagedPersons && stagedPersons.length > 0) {
            if (this.store.selectSnapshot(PwaState.isMobile)) {
                this.noScroll = true;
                this.alertModal.openModal();
            } else {
                this.abortClear();
            }
        } else {
            this.close();
        }
    }

    public continue(): void {
        this.noScroll = false;
    }

    public abortClear(): void {
        this.store.dispatch(new PermitTeamActions.UnstageAll());
        this.close();
    }

    public fireWatcherNotNeededChange(notNeeded: boolean) {
        if (notNeeded) {
            this.abort();
        } else {
            this.currentView = AddPersonView.LastUsed;
        }
    }

    public showAddNew(): void {
        this.currentView = this.store.selectSnapshot(PermitCreateSelectors.isFlammableHotWork)
            ? AddPersonView.Search
            : AddPersonView.Text;
    }

    public unstagePerson(certificateId: number) {
        this.store.dispatch(new PermitTeamActions.UnstagePerson(certificateId));
    }

    private close() {
        this.store.dispatch(new PermitTeamActions.SetAddPersonView(undefined));
        this.closeModal();
    }

    public showCreateCertificate(person: PermitPersonWithStatus) {
        this.person = person;
        this.requireEmailAddress = [null, undefined, ""].includes(this.person.email);
        this.currentView = AddPersonView.CreateCertificate;
    }

    public updateForm(form: CreateCertificateForm) {
        this.form = form;
        this.formValid = form.formValid;
    }

    public closeCreateCertificate() {
        this.store.dispatch(new PermitTeamActions.ClearCreateCertificateResponse());
        this.currentView = AddPersonView.LastUsed;
    }

    public submitCreateCertificate() {
        const command = this.form as CreateCertificateCommand;
        command.oldCertificateId = this.person.certificateId;
        this.store.dispatch(
            new PermitTeamActions.CreateNewCertificateAndStagePerson(this.permitId, this.person, command),
        ).pipe(untilDestroyed(this)).subscribe(() => {
            const response = this.store.selectSnapshot(PermitTeamSelectors.createCertificateResponse);
            if (response.success) {
                this.closeCreateCertificate();
                this.cdr.detectChanges();
            }
        });
    }
}
